/**
 * 회원 상세 - "기본 정보 2" 탭
 *
 * 포함:
 *  - 회원 확장 필드 (생년월일/회원구분/준비시험/계열)
 *  - 학부모 정보 요약 (parent 탭 연결)
 *  - 학생 성적 (국/수/영/한/탐, 회차 누적)
 *  - 목표 대학 1·2·3순위 (자동완성, 합격자료 표시)
 *  - 등록 절차 체크리스트
 */

import React, { useEffect, useState, useCallback } from 'react';
import { TagIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../../../infrastructure/api';

interface Props {
  userId: string;
  onGotoParentTab?: () => void;
}

interface Basic2 {
  birth_date?: string | null;
  member_category?: string | null;
  prep_exam?: string | null;
  track?: string | null;
}

interface GradeRecord {
  id?: string;
  exam_label?: string | null;
  korean_score?: number | null;
  korean_grade?: number | null;
  math_score?: number | null;
  math_grade?: number | null;
  english_score?: number | null;
  english_grade?: number | null;
  history_score?: number | null;
  history_grade?: number | null;
  inquiry_score?: number | null;
  inquiry_grade?: number | null;
  inquiry_subjects?: string | null;
  note?: string | null;
  created_at?: string | null;
}

interface UniversityItem {
  id: string;
  name: string;
  region?: string | null;
  type?: string | null;
  univ_cd?: string | null;
  has_admission_data?: boolean;
}

interface TargetUniv {
  id?: string;
  rank: number;
  university_id?: string | null;
  university_name?: string | null;
  department?: string | null;
  note?: string | null;
}

interface Checklist {
  payment_done: boolean;
  payment_done_at?: string | null;
  app_signup_done: boolean;
  app_signup_done_at?: string | null;
  agreement_received: boolean;
  agreement_received_at?: string | null;
  note?: string | null;
}

const MEMBER_CATEGORIES = ['고등학생', '중학생', '초등학생', 'N수생', '성인 수험생', '전문직', '기타'];
const TRACKS = ['문과', '이과', '예체능', '미정'];

export default function MemberBasicInfo2Tab({ userId, onGotoParentTab }: Props) {
  const [basic, setBasic] = useState<Basic2>({});
  const [grades, setGrades] = useState<GradeRecord[]>([]);
  const [targets, setTargets] = useState<TargetUniv[]>([
    { rank: 1 }, { rank: 2 }, { rank: 3 },
  ]);
  const [checklist, setChecklist] = useState<Checklist>({
    payment_done: false, app_signup_done: false, agreement_received: false,
  });
  const [admissionData, setAdmissionData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingBasic, setSavingBasic] = useState(false);
  const [showGradeForm, setShowGradeForm] = useState(false);
  const [newGrade, setNewGrade] = useState<GradeRecord>({});

  // 대학 검색 state (rank별)
  const [searchQ, setSearchQ] = useState<{ [rank: number]: string }>({ 1: '', 2: '', 3: '' });
  const [searchResults, setSearchResults] = useState<{ [rank: number]: UniversityItem[] }>({});
  const [searchOpen, setSearchOpen] = useState<{ [rank: number]: boolean }>({});

  // 데이터 로드
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [b, g, t, c] = await Promise.all([
        apiClient.get(`/v1/owner/members-extended/${userId}/basic2`),
        apiClient.get(`/v1/owner/members-extended/${userId}/grades`),
        apiClient.get(`/v1/owner/members-extended/${userId}/target-universities`),
        apiClient.get(`/v1/owner/members-extended/${userId}/checklist`),
      ]);
      setBasic(b.data || {});
      setGrades(g.data || []);
      // 1·2·3순위 정규화
      const fetched = (t.data || []) as TargetUniv[];
      const normalized = [1, 2, 3].map(rank =>
        fetched.find(x => x.rank === rank) || { rank }
      );
      setTargets(normalized);
      setChecklist({
        payment_done: c.data?.payment_done ?? false,
        payment_done_at: c.data?.payment_done_at,
        app_signup_done: c.data?.app_signup_done ?? false,
        app_signup_done_at: c.data?.app_signup_done_at,
        agreement_received: c.data?.agreement_received ?? false,
        agreement_received_at: c.data?.agreement_received_at,
        note: c.data?.note,
      });
    } catch (e) {
      console.error('[Basic2] load error', e);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // 합격 자료 별도 로드 (목표대학 변경 후)
  const loadAdmission = useCallback(async () => {
    try {
      const res = await apiClient.get(`/v1/owner/members-extended/${userId}/admission-insights`);
      setAdmissionData(res.data?.targets || []);
    } catch {}
  }, [userId]);

  useEffect(() => { loadAdmission(); }, [loadAdmission, targets]);

  // 기본 정보 저장
  const handleSaveBasic = async () => {
    setSavingBasic(true);
    try {
      await apiClient.patch(`/v1/owner/members-extended/${userId}/basic2`, basic);
    } catch (e: any) {
      alert('저장 실패: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingBasic(false);
    }
  };

  // 성적 추가
  const handleAddGrade = async () => {
    try {
      await apiClient.post(`/v1/owner/members-extended/${userId}/grades`, newGrade);
      setNewGrade({});
      setShowGradeForm(false);
      loadAll();
    } catch (e: any) {
      alert('추가 실패: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // 성적 삭제
  const handleDeleteGrade = async (id: string) => {
    if (!window.confirm('이 성적을 삭제하시겠습니까?')) return;
    try {
      await apiClient.delete(`/v1/owner/members-extended/${userId}/grades/${id}`);
      loadAll();
    } catch (e: any) {
      alert('삭제 실패: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // 대학 검색
  const handleSearchUniv = async (rank: number, q: string) => {
    setSearchQ(prev => ({ ...prev, [rank]: q }));
    if (q.length < 1) {
      setSearchResults(prev => ({ ...prev, [rank]: [] }));
      return;
    }
    try {
      const res = await apiClient.get(`/v1/owner/members-extended/universities/search?q=${encodeURIComponent(q)}&limit=15`);
      setSearchResults(prev => ({ ...prev, [rank]: res.data || [] }));
      setSearchOpen(prev => ({ ...prev, [rank]: true }));
    } catch {}
  };

  // 대학 선택
  const handleSelectUniv = (rank: number, univ: UniversityItem) => {
    setTargets(prev => prev.map(t =>
      t.rank === rank ? { ...t, university_id: univ.id, university_name: univ.name } : t
    ));
    setSearchQ(prev => ({ ...prev, [rank]: univ.name }));
    setSearchOpen(prev => ({ ...prev, [rank]: false }));
  };

  // 목표대학 저장
  const handleSaveTargets = async () => {
    try {
      await apiClient.put(`/v1/owner/members-extended/${userId}/target-universities`, targets);
      loadAdmission();
      alert('목표대학이 저장되었습니다.');
    } catch (e: any) {
      alert('저장 실패: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // 체크리스트 토글
  const handleToggleChecklist = async (key: keyof Checklist, value: boolean) => {
    try {
      const res = await apiClient.patch(`/v1/owner/members-extended/${userId}/checklist`, { [key]: value });
      setChecklist({
        payment_done: res.data?.payment_done ?? false,
        payment_done_at: res.data?.payment_done_at,
        app_signup_done: res.data?.app_signup_done ?? false,
        app_signup_done_at: res.data?.app_signup_done_at,
        agreement_received: res.data?.agreement_received ?? false,
        agreement_received_at: res.data?.agreement_received_at,
        note: res.data?.note,
      });
    } catch (e: any) {
      alert('변경 실패: ' + (e?.response?.data?.detail || e.message));
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">로딩 중...</div>;
  }

  return (
    <div className="space-y-6">
      {/* ===== 학습 정보 (계열 + 준비 시험) - 기본정보 1과 일관성 ===== */}
      <section>
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
          📋 학습 정보
        </h4>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2">
          {/* 계열 */}
          <div className="flex items-center text-sm min-h-[32px]">
            <TagIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
            <span className="text-gray-500 mr-2 whitespace-nowrap flex-shrink-0">계열:</span>
            <select
              value={basic.track || ''}
              onChange={(e) => setBasic(p => ({ ...p, track: e.target.value }))}
              className="px-2 py-1 border border-gray-300 rounded text-sm w-32"
            >
              <option value="">선택</option>
              {TRACKS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {/* 준비 시험 */}
          <div className="flex items-center text-sm min-h-[32px]">
            <ClipboardDocumentIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
            <span className="text-gray-500 mr-2 whitespace-nowrap flex-shrink-0">준비 시험:</span>
            <input
              type="text"
              value={basic.prep_exam || ''}
              onChange={(e) => setBasic(p => ({ ...p, prep_exam: e.target.value }))}
              placeholder="예: 9급 공무원"
              className="px-2 py-1 border border-gray-300 rounded text-sm flex-1"
            />
          </div>
        </div>
        <div className="mt-2 text-right">
          <button
            onClick={handleSaveBasic}
            disabled={savingBasic}
            className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {savingBasic ? '저장 중...' : '저장'}
          </button>
        </div>
      </section>

      {/* ===== 성적 ===== */}
      <section className="pt-4 mt-4 border-t border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-700 flex items-center">📝 성적</h4>
          <button
            onClick={() => setShowGradeForm(!showGradeForm)}
            className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
          >
            {showGradeForm ? '취소' : '+ 성적 추가'}
          </button>
        </div>

        {showGradeForm && (
          <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-3">
            <input
              type="text"
              placeholder="시험 회차 (예: 2026년 3월 모의고사)"
              value={newGrade.exam_label || ''}
              onChange={(e) => setNewGrade(p => ({ ...p, exam_label: e.target.value }))}
              className="w-full mb-2 px-2 py-1 border border-gray-300 rounded text-sm"
            />
            <div className="grid grid-cols-5 gap-2">
              {[
                { key: 'korean', label: '국어' },
                { key: 'math', label: '수학' },
                { key: 'english', label: '영어' },
                { key: 'history', label: '한국사' },
                { key: 'inquiry', label: '탐구' },
              ].map(s => (
                <div key={s.key}>
                  <label className="text-[10px] text-gray-500">{s.label}</label>
                  <div className="flex gap-1">
                    <input
                      type="number"
                      placeholder="점수"
                      value={(newGrade as any)[`${s.key}_score`] ?? ''}
                      onChange={(e) => setNewGrade(p => ({ ...p, [`${s.key}_score`]: e.target.value === '' ? null : Number(e.target.value) }))}
                      className="w-full px-1 py-1 border border-gray-300 rounded text-xs"
                    />
                    <input
                      type="number"
                      placeholder="등급"
                      min={1}
                      max={9}
                      value={(newGrade as any)[`${s.key}_grade`] ?? ''}
                      onChange={(e) => setNewGrade(p => ({ ...p, [`${s.key}_grade`]: e.target.value === '' ? null : Number(e.target.value) }))}
                      className="w-12 px-1 py-1 border border-gray-300 rounded text-xs"
                    />
                  </div>
                </div>
              ))}
            </div>
            <input
              type="text"
              placeholder="탐구 과목명 (예: 생활과윤리, 사회문화)"
              value={newGrade.inquiry_subjects || ''}
              onChange={(e) => setNewGrade(p => ({ ...p, inquiry_subjects: e.target.value }))}
              className="w-full mt-2 px-2 py-1 border border-gray-300 rounded text-sm"
            />
            <div className="mt-2 text-right">
              <button
                onClick={handleAddGrade}
                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
              >
                저장
              </button>
            </div>
          </div>
        )}

        {grades.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">등록된 성적이 없습니다.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-1.5 text-left text-gray-500">회차</th>
                <th className="px-2 py-1.5 text-center text-gray-500">국어</th>
                <th className="px-2 py-1.5 text-center text-gray-500">수학</th>
                <th className="px-2 py-1.5 text-center text-gray-500">영어</th>
                <th className="px-2 py-1.5 text-center text-gray-500">한국사</th>
                <th className="px-2 py-1.5 text-center text-gray-500">탐구</th>
                <th className="px-2 py-1.5 text-center text-gray-500">날짜</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {grades.map(g => {
                const cell = (s: number | null | undefined, gr: number | null | undefined) => {
                  if (s == null && gr == null) return '-';
                  const parts = [];
                  if (s != null) parts.push(`${s}점`);
                  if (gr != null) parts.push(`${gr}등급`);
                  return parts.join(' / ');
                };
                return (
                  <tr key={g.id} className="border-t border-gray-100">
                    <td className="px-2 py-1.5">{g.exam_label || '-'}</td>
                    <td className="px-2 py-1.5 text-center">{cell(g.korean_score, g.korean_grade)}</td>
                    <td className="px-2 py-1.5 text-center">{cell(g.math_score, g.math_grade)}</td>
                    <td className="px-2 py-1.5 text-center">{cell(g.english_score, g.english_grade)}</td>
                    <td className="px-2 py-1.5 text-center">{cell(g.history_score, g.history_grade)}</td>
                    <td className="px-2 py-1.5 text-center">
                      {cell(g.inquiry_score, g.inquiry_grade)}
                      {g.inquiry_subjects && <div className="text-[10px] text-gray-400">{g.inquiry_subjects}</div>}
                    </td>
                    <td className="px-2 py-1.5 text-center text-gray-400">
                      {g.created_at ? g.created_at.split('T')[0] : '-'}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <button
                        onClick={() => g.id && handleDeleteGrade(g.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* ===== 목표 대학 ===== */}
      <section className="pt-4 mt-4 border-t border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-700 flex items-center">🎯 목표 대학 (1·2·3순위)</h4>
          <button
            onClick={handleSaveTargets}
            className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
          >
            저장
          </button>
        </div>
        <div className="space-y-2">
          {[1, 2, 3].map(rank => {
            const t = targets.find(x => x.rank === rank) || { rank };
            return (
              <div key={rank} className="flex items-center gap-2 text-sm">
                <span className="text-gray-500 w-12 flex-shrink-0">{rank}순위</span>
                <input
                  type="text"
                  value={t.university_name || ''}
                  onChange={(e) => setTargets(prev => prev.map(x =>
                    x.rank === rank ? { ...x, university_id: null, university_name: e.target.value } : x
                  ))}
                  placeholder="대학교 이름"
                  className="px-2 py-1 border border-gray-300 rounded text-sm flex-1"
                />
                <input
                  type="text"
                  value={t.department || ''}
                  onChange={(e) => setTargets(prev => prev.map(x =>
                    x.rank === rank ? { ...x, department: e.target.value } : x
                  ))}
                  placeholder="학과/학부"
                  className="w-40 px-2 py-1 border border-gray-300 rounded text-sm"
                />
              </div>
            );
          })}
        </div>
      </section>

    </div>
  );
}
